import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useBranding } from '../context/BrandingContext'
import { authApi } from '../services/api'
import { isReadOnlyUser } from '../utils/readerAccess'
import { useFeatures } from '../contexts/FeaturesContext'
import defaultLogoImage from '../assets/xtract-symbol-dark.png'
import NotificationBell from './NotificationBell'
import { BreadcrumbContext } from '../context/BreadcrumbContext'
import UserAvatar from './UserAvatar'
import ThemePopover from './ThemePopover'
import SubscriptionBanner from './SubscriptionBanner'
import WorkspaceSwitcher from './WorkspaceSwitcher'
import OpCoForbidden, { hasNoOpcoAccess } from '../pages/OpCoForbidden'
import { useOpCo } from '../context/OpCoContext'
import {
  LayoutDashboard,
  FileText,
  Files,
  Mail,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Activity,
  FileStack,
  Settings,
  Webhook,
  Server,
  Users,
  Wallet,
  BookOpen,
  CheckCircle,
  Sparkles,
  GraduationCap,
} from 'lucide-react'
import SuperAdminTag from './opco/SuperAdminTag'

const navigationSections = [
  {
    title: 'Workspace',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Templates', href: '/templates', icon: FileStack, roles: ['admin', 'template_manager'] },
      { name: 'Requests', href: '/requests', icon: FileText },
      { name: 'Approvals', href: '/approvals', icon: CheckCircle, hiddenForRoles: ['sender'] },
      { name: 'Email Queue', href: '/emails', icon: Mail },
      { name: 'Documents', href: '/documents', icon: Files },
      { name: 'Activity', href: '/activity', icon: Activity, adminOnly: true },
    ],
  },
  {
    title: 'Administration',
    items: [
      { name: 'Connectors', href: '/webhooks', icon: Webhook, adminOnly: true, feature: 'connectors' },
      { name: 'Output Destinations', href: '/output-destinations', icon: Server, adminOnly: true, feature: 'connectors' },
      { name: 'Billing & Analytics', href: '/billing', icon: Wallet, adminOnly: true },
      { name: 'Users & Groups', href: '/users-groups', icon: Users, adminOnly: true, opcoManagers: true },
      { name: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
    ],
  },
  {
    title: 'Learning',
    items: [
      { name: 'Learning', href: '/learning', icon: GraduationCap },
    ],
  },
  {
    title: 'Help',
    items: [
      { name: "What's New", href: '/whats-new', icon: Sparkles },
      { name: 'Guide', href: '/guide', icon: BookOpen },
    ],
  },
]

// Helper: build breadcrumbs from pathname
function getBreadcrumbs(pathname, search = '') {
  if (pathname === '/') return [{ label: 'Dashboard', href: '/' }]
  const segments = pathname.split('/').filter(Boolean)
  const crumbs = [{ label: 'Dashboard', href: '/' }]
  const query = new URLSearchParams(search)
  const nameMap = {
    requests: 'Requests',
    emails: 'Email Queue',
    documents: 'Documents',
    jobs: 'Jobs',
    issuers: 'Issuers',
    templates: 'Templates',
    approvals: 'Approvals',
    activity: 'Activity',
    webhooks: 'Connectors',
    'output-destinations': 'Output Destinations',
    billing: 'Billing & Analytics',
    settings: 'Settings',
    guide: 'Guide',
    'contact-support': 'Contact & Support',
    'whats-new': "What's New",
    learning: 'Learning',
    'test-analysis': 'Analyze & Test',
  }
  if (segments[0]) {
    crumbs.push({ label: nameMap[segments[0]] || segments[0], href: `/${segments[0]}` })
  }

  // Templates create flow: Dashboard > Templates > Create template
  if (segments[0] === 'templates' && !segments[1] && query.get('create') === '1') {
    crumbs.push({ label: 'Create template', href: '/templates?create=1' })
    return crumbs
  }

  // Requests create flow: Dashboard > Requests > New Request
  if (segments[0] === 'requests' && !segments[1] && query.get('create') === '1') {
    crumbs.push({ label: 'New Request', href: '/requests?create=1' })
    return crumbs
  }

  // Learning pages name their own view (guide title, Manage) via usePageBreadcrumbs.
  if (segments[0] === 'learning') return crumbs

  // Detail page: show ID shortened
  if (segments[1]) {
    const id = segments[1]
    const shortId = id.length > 8 ? `#${id.substring(0, 8)}…` : `#${id}`
    crumbs.push({ label: shortId, href: pathname })
  }
  return crumbs
}

// User account menu shown at the bottom of the sidebar. Self-contained (own open
// state + outside-click). Dropdown is portalled to <body> so it isn't clipped by
// the sidebar's overflow, and it opens upward from the trigger.
function SidebarUserMenu({ collapsed }) {
  const { user, logout } = useAuth()
  const { enabled: opcoEnabled, opcos, isOpcoAdmin } = useOpCo()
  // Single-OpCo users have no header switcher: show their OpCo here instead.
  const singleOpco = opcoEnabled && !isOpcoAdmin && opcos.length === 1 ? opcos[0] : null
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ left: 0, bottom: 0, width: 264, flyout: false })
  const btnRef = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const update = () => {
      const btnRect = btnRef.current?.getBoundingClientRect()
      const shellRect = btnRef.current?.closest('[data-sidebar-shell]')?.getBoundingClientRect()
      if (!btnRect) return
      const bottom = window.innerHeight - btnRect.top + 8
      // Collapsed rail is too narrow to hold the card - fly it out to the right of
      // the rail instead of stretching past it. Expanded sidebar: keep the card
      // flush inside the sidebar's own width so it never spills into the page content.
      if (collapsed && shellRect) {
        setPos({ left: shellRect.right + 8, bottom, width: 280, flyout: true })
      } else if (shellRect) {
        const width = Math.min(280, shellRect.width - 16)
        setPos({ left: shellRect.left + (shellRect.width - width) / 2, bottom, width, flyout: false })
      } else {
        setPos({ left: btnRect.left, bottom, width: 264, flyout: false })
      }
    }
    update()
    const onDocMouseDown = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, collapsed])

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        title={collapsed ? (user?.name || user?.email) : undefined}
        aria-label="User menu"
        aria-expanded={open}
        className={`w-full flex items-center rounded-lg text-white transition-colors hover:bg-white/10 ${collapsed ? 'justify-center p-2' : 'gap-2.5 px-2 py-2'}`}
      >
        <UserAvatar user={user} size="sm" />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 text-left">
              <span className="block text-sm font-medium text-white truncate">{user?.name || user?.email}</span>
              {user?.role && (
                <span className="block text-[11px] text-white/50 truncate capitalize">
                  {opcoEnabled && isOpcoAdmin ? 'Super Admin' : user.role}
                </span>
              )}
            </span>
            <ChevronDown className={`h-4 w-4 text-white/50 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', left: pos.left, bottom: pos.bottom, width: pos.width }}
          className="origin-bottom-left animate-fade-in bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black/5 border border-gray-200 dark:border-gray-700 overflow-hidden z-[60]"
        >
          {pos.flyout && (
            <span
              aria-hidden="true"
              className="absolute -left-1.5 bottom-4 h-3 w-3 rotate-45 bg-white dark:bg-gray-800 border-l border-b border-gray-200 dark:border-gray-700"
            />
          )}
          {/* User info header */}
          <div className="px-4 py-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-850 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <UserAvatar user={user} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{user?.name}</p>
                  {user?.role && !(opcoEnabled && isOpcoAdmin) && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 ${
                      user.role === 'admin'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {user.role}
                    </span>
                  )}
                  {opcoEnabled && isOpcoAdmin && <SuperAdminTag compact />}
                </div>
                {user?.job_title && (
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-300 truncate">{user.job_title}</p>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{user?.email}</p>
                {singleOpco && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">OpCo: {singleOpco.name}</p>
                )}
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="p-1.5">
            <button
              onClick={() => { setOpen(false); logout() }}
              className="flex items-center w-full px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors gap-2.5"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

function Layout() {
  const { user, logout, organizationName } = useAuth()
  const features = useFeatures()
  const opcoState = useOpCo()
  const { branding, BRANDING_DEFAULTS } = useBranding()
  const location = useLocation()
  const navigate = useNavigate()
  const configuredLogo = (branding.logo_url || '').trim()
  const logoLooksLikeFavicon = /(^|\/)favicon([.-]|$)/i.test(configuredLogo)
  const logoImage = (!configuredLogo || logoLooksLikeFavicon)
    ? (BRANDING_DEFAULTS.logo_url || defaultLogoImage)
    : configuredLogo
  const appName = branding.app_name || 'Xtract'
  const appSubtitle = branding.subtitle || 'Synapx'
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [expandedSections, setExpandedSections] = useState(() =>
    Object.fromEntries(navigationSections.map((section) => [section.title, true]))
  )

  // OPCO_FORBIDDEN from the API (see services/api.js): show the access page
  // until the user navigates elsewhere.
  const [opcoForbidden, setOpcoForbidden] = useState(false)
  useEffect(() => {
    if (!opcoState.enabled) return undefined
    const onForbidden = () => setOpcoForbidden(true)
    window.addEventListener('opco-forbidden', onForbidden)
    return () => window.removeEventListener('opco-forbidden', onForbidden)
  }, [opcoState.enabled])
  useEffect(() => { setOpcoForbidden(false) }, [location.pathname])
  const noOpcoAccess = hasNoOpcoAccess(opcoState)

  const breadcrumbs = getBreadcrumbs(location.pathname, location.search)
  // Views inside a single route (set by the page via usePageBreadcrumbs).
  const [pageTrail, setPageTrail] = useState(null)
  
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const handleNavClick = (e, item) => {
    // Clicking the same sidebar item should still notify the current page to reset transient UI state.
    if (location.pathname === item.href) {
      e.preventDefault()
      navigate(item.href, {
        replace: false,
        state: {
          fromSidebar: true,
          navClickAt: Date.now(),
        },
      })
    }
    setSidebarOpen(false)
  }
  
  const canAccessNavItem = (item) => {
    // A switched-off feature hides the entry for everyone, so this is checked
    // before any role rule (readers are otherwise allowed past those).
    if (item.feature && features[item.feature] === false) return false
    // OpCo admins / managers reach Users & Groups for its OpCos tab.
    if (item.opcoManagers && opcoState.enabled && opcoState.isOpcoAdmin) return true
    const isReader = isReadOnlyUser(user)
    if (isReader && (item.adminOnly || item.roles)) return true
    if (item.roles) return item.roles.includes(user?.role)
    if (item.adminOnly && user?.role !== 'admin') return false
    if (item.hiddenForRoles && item.hiddenForRoles.includes(user?.role)) return false
    return true
  }

  const visibleSections = navigationSections
    .map((section) => ({
      ...section,
      items: section.items.filter(canAccessNavItem),
    }))
    .filter((section) => section.items.length > 0)

  const visibleNavItems = visibleSections.flatMap((section) => section.items)

  const isSectionExpanded = (sectionTitle) => expandedSections[sectionTitle] !== false

  const toggleSection = (sectionTitle) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionTitle]: prev[sectionTitle] === false,
    }))
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 dark:bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Mobile sidebar */}
      <div
        data-sidebar-shell
        className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col transform transition-transform lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2">
            <img src={logoImage} alt={appName} className="w-8 h-8 rounded-lg object-contain" />
            <span className="text-lg font-semibold text-white">{appName}</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-md text-white/60 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="mt-4 px-3 flex-1 overflow-y-auto sidebar-scroll">
          {visibleSections.map((section) => (
            <div key={section.title} className="mb-5">
              <button
                type="button"
                onClick={() => toggleSection(section.title)}
                aria-expanded={isSectionExpanded(section.title)}
                className="w-full px-3 mb-2 flex items-center justify-between text-[11px] uppercase tracking-wider font-semibold text-white/40 hover:text-white/70 transition-colors"
              >
                <span>{section.title}</span>
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isSectionExpanded(section.title) ? 'rotate-90 text-white/60' : 'text-white/30'}`} />
              </button>
              {isSectionExpanded(section.title) && section.items.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`relative flex items-center px-3 py-2.5 mb-1 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {isActive(item.href) && <span className="nav-active-indicator" />}
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        {/* User account at bottom (mobile) */}
        <div className="border-t border-white/10 px-3 py-2 flex-shrink-0">
          <SidebarUserMenu collapsed={false} />
        </div>
      </div>
      
      {/* Desktop sidebar */}
      <div data-sidebar-shell className={`hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:flex-col transition-[width] duration-300 ease-in-out ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <div className="flex flex-col flex-grow overflow-hidden" style={{ backgroundColor: 'var(--sidebar-bg)' }}>
          {/* Logo + collapse toggle */}
          <div className={`flex items-center h-16 border-b border-white/10 flex-shrink-0 ${sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'}`}>
            <div className={`flex items-center gap-2 ${sidebarCollapsed ? '' : 'overflow-hidden'}`}>
              <img
                src={logoImage}
                alt={appName}
                className="w-8 h-8 rounded-lg object-contain flex-shrink-0 cursor-pointer"
                onClick={sidebarCollapsed ? () => setSidebarCollapsed(false) : undefined}
                title={sidebarCollapsed ? 'Expand sidebar' : undefined}
              />
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <span className="text-lg font-semibold text-white truncate block">{appName}</span>
                  <p className="text-xs text-white/50 truncate">{appSubtitle}</p>
                </div>
              )}
            </div>
            {!sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(c => !c)}
                className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
                title="Collapse sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 mt-4 px-3 overflow-y-auto sidebar-scroll">
            {sidebarCollapsed ? (
              visibleNavItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  title={item.name}
                  className={`relative flex items-center justify-center px-0 py-2.5 mb-1 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {isActive(item.href) && <span className="nav-active-indicator" />}
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                </Link>
              ))
            ) : (
              visibleSections.map((section) => (
                <div key={section.title} className="mb-5">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.title)}
                    aria-expanded={isSectionExpanded(section.title)}
                    className="w-full px-3 mb-2 flex items-center justify-between text-[11px] uppercase tracking-wider font-semibold text-white/40 hover:text-white/70 transition-colors"
                  >
                    <span>{section.title}</span>
                    <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isSectionExpanded(section.title) ? 'rotate-90 text-white/60' : 'text-white/30'}`} />
                  </button>
                  {isSectionExpanded(section.title) && section.items.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={(e) => handleNavClick(e, item)}
                      className={`relative flex items-center px-3 py-2.5 mb-1 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.href)
                          ? 'bg-white/10 text-white'
                          : 'text-white/60 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {isActive(item.href) && <span className="nav-active-indicator" />}
                      <item.icon className="h-5 w-5 flex-shrink-0 mr-3" />
                      {item.name}
                    </Link>
                  ))}
                </div>
              ))
            )}
          </nav>
          
          {/* User account (moved from top header to sidebar bottom) */}
          <div className={`border-t border-white/10 flex-shrink-0 ${sidebarCollapsed ? 'px-2 py-2' : 'px-3 py-2'}`}>
            <SidebarUserMenu collapsed={sidebarCollapsed} />
          </div>

          {/* Sidebar footer */}
          <div className="px-4 py-3 border-t border-white/10 flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(45,212,191,0.12) 0%, transparent 100%)' }}>
            {!sidebarCollapsed ? (
              <a href="https://www.synapx.com/xtract/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 hover:opacity-80 transition-opacity">
                <span className="text-[11px] text-white">Powered by</span>
                <span className="text-[11px] font-semibold" style={{ color: '#2DD4BF' }}>Synapx</span>
              </a>
            ) : (
              <a href="https://www.synapx.com/xtract/" target="_blank" rel="noopener noreferrer" className="flex justify-center hover:opacity-80 transition-opacity">
                <span className="text-[10px] font-bold" style={{ color: '#2DD4BF' }}>S</span>
              </a>
            )}
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className={`transition-[padding] duration-300 ease-in-out ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Top header */}
        <header className="app-header sticky top-0 z-20 flex items-center justify-between h-16 px-6 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
              aria-label="Open sidebar menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Breadcrumbs */}
            {location.pathname !== '/' && (
              <nav className="hidden lg:flex items-center text-sm" aria-label="Breadcrumb">
                {breadcrumbs.map((crumb, i) => (
                  <React.Fragment key={crumb.href}>
                    {i > 0 && <ChevronRight className="h-3.5 w-3.5 breadcrumb-separator" />}
                    {i === breadcrumbs.length - 1 && !pageTrail ? (
                      <span className="text-gray-500 dark:text-gray-400 font-medium">{crumb.label}</span>
                    ) : (
                      <Link
                        to={crumb.href}
                        // With a page trail the section crumb is the way back to
                        // the page's root view: same reset signal as a sidebar click.
                        state={i === breadcrumbs.length - 1 ? { fromSidebar: true, navClickAt: Date.now() } : undefined}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </React.Fragment>
                ))}
                {pageTrail?.map((item, i) => (
                  <React.Fragment key={`trail-${i}-${item.label}`}>
                    <ChevronRight className="h-3.5 w-3.5 breadcrumb-separator" />
                    {item.onClick ? (
                      <button
                        type="button"
                        onClick={item.onClick}
                        className="max-w-[16rem] truncate text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        {item.label}
                      </button>
                    ) : (
                      <span aria-current="page" className="max-w-[20rem] truncate text-gray-500 dark:text-gray-400 font-medium animate-panel-in">{item.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* OpCo workspace switcher (renders nothing with OpCos off) */}
            <WorkspaceSwitcher />

            {/* Appearance: light/dark/system + dark-mode contrast intensity */}
            <ThemePopover />

            {/* Notification bell */}
            <NotificationBell />
          </div>
        </header>
        
        {/* Page content */}
        <main className="app-main p-4 sm:p-6 min-h-[calc(100vh-4rem)]">
          <SubscriptionBanner />
          {user?.needs_admin && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 px-4 py-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>No admin found.</strong> Your organisation has no administrator. Would you like to become the admin?
              </p>
              <button
                onClick={async () => {
                  try {
                    await authApi.claimAdmin()
                    window.location.reload()
                  } catch (e) {
                    console.error('Failed to claim admin:', e)
                  }
                }}
                className="ml-4 shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
              >
                Become Admin
              </button>
            </div>
          )}
          <BreadcrumbContext.Provider value={setPageTrail}>
            {noOpcoAccess
              ? <OpCoForbidden variant="no-access" />
              : opcoForbidden
                ? <OpCoForbidden variant="forbidden" />
                : <Outlet />}
          </BreadcrumbContext.Provider>
        </main>
      </div>
      
    </div>
  )
}

export default Layout
